import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { useAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { noop } from '@b-stock/fe-core/bstock-react'
import type { StoreFrontDto } from '@b-stock/listing-api-client'

import usePusherLegacy from '@/buyer-portal/helpers/usePusher'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import type {
  SearchParams,
  SearchResultsPage,
} from '@/queries/listingSearchQuery'
import listingSearchQuery from '@/queries/listingSearchQuery'
import newSellerStorefrontsQuery from '@/queries/newSellerStorefrontsQuery'

import { defaultState } from '../search/ListingSearchProvider/defaultState'

const useSearchParams = (storefront: StoreFrontDto): SearchParams => {
  return useMemo(
    () => ({
      params: {
        // Storefront should have an id... but just in case it would be
        // safer to show nothing than listings from all sellers.
        storeFrontId: [storefront._id ?? '---'],
        offset: 0,
        // Limit to 5 instead of 4 because we might be stripping one of the results
        // out after-the-fact, but still want to show at 4 if there are at least 4
        // non-excluded listings
        limit: 5,
      },
      fetchFilters: false,
      usePostEndpoint: true,
    }),
    [storefront]
  )
}

const useFilteredData = ({
  excludeListingId,
  searchParams,
}: {
  excludeListingId: string
  searchParams: SearchParams
}) => {
  const { data, isLoading } = listingSearchQuery.useWithOpts([searchParams], {
    // If a user clicks from one listing into another for the same seller,
    // time will have passed and there are likely to be new and/or updated
    // listings.
    // Also, since the component wasn't visible, the listeners that would
    // update the underlying query data in response to pusher events
    // won't happen, so if we kept the old data around it would contribute
    // to bugs like https://bstock.atlassian.net/browse/TBD-2588
    staleTime: 1000,
  })
  const filteredListings = useMemo(
    () =>
      (
        data?.listings.filter((listing) => listing.id !== excludeListingId) ??
        []
      )
        // We fetch (up to) 5, but want to show 4 (or less), regardless of if
        // `excludedListingId` is in the 5 results that were fetched or not.
        .slice(0, 4),
    [data, excludeListingId]
  )
  return useMemo(
    () => ({
      isLoading,
      data: data
        ? {
            ...data,
            listings: filteredListings,
          }
        : null,
    }),
    [data, filteredListings, isLoading]
  )
}

/**
 * Provides a limited (no search/filter/sort/etc capabilities) value for ListingSearchContext.
 *
 * Why don't we just use ListingSearchProvider?
 * Because that provider adds a lot of extra details and functionality related to managing
 * the user-provided search, filter, sort, etc data.
 * In the "More from this seller" block, there is no configurable values from the user,
 * we just always get the "first 5" listings for a given storefront.
 * Unfortunately, ListingSearchContext is also used for several other purposes, and some
 * of the child components of ListingGrid end up referencing the ListingSearchContext,
 * so we can't just omit it here.
 */
export const useLimitedListingSearchContext = ({
  storefront,
  excludeListingId,
}: {
  storefront: StoreFrontDto
  excludeListingId: string
}) => {
  const searchParams = useSearchParams(storefront)
  const { data, isLoading } = useFilteredData({
    excludeListingId,
    searchParams,
  })

  const pusherClient3MP = usePusherLegacy()

  const queryClient = useQueryClient()
  const accessToken = useAccessToken()
  const updateSearchData = useCallback(
    (updater: (data: SearchResultsPage) => SearchResultsPage) => {
      queryClient.setQueryData<SearchResultsPage>(
        listingSearchQuery.getKey(accessToken, [searchParams]),
        (oldData) => {
          if (!oldData) {
            return undefined
          }

          return updater(oldData)
        }
      )
    },
    [searchParams, accessToken, queryClient]
  )

  const { track } = useAnalytics()
  const isNewSellerTreatment =
    useFlag(FeatureFlags.NEW_SELLER_ALP_LABEL) === 'treatment'
  const { data: newSellerIds } = newSellerStorefrontsQuery.useWithOpts([], {
    enabled: isNewSellerTreatment,
  })
  const impressionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [currentImpressionId, setCurrentImpressionId] = useState<string | null>(
    null
  )

  // Track impressions when listings load
  useEffect(() => {
    if (!isLoading && data?.listings.length) {
      // Clear any existing timeout
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
      }

      // Set new timeout for impression tracking
      impressionTimeoutRef.current = setTimeout(() => {
        if (data.listings.length) {
          const impressionId = crypto.randomUUID()
          setCurrentImpressionId(impressionId)

          track('listing_card_impression', {
            listing_card_impression_id: impressionId,
            listings_count: data.listings.length,
            carousel_name: 'More from this Seller',
            screen_name: 'listing_detail_page',
            listings: data.listings.map((listing) => ({
              id: listing.id,
              recommended_rank: listing.customRank,
              is_control: listing.randomSetLabel,
              is_new_seller:
                isNewSellerTreatment &&
                (newSellerIds?.has(listing.storefrontId) ?? false),
            })),
          })
        }
      }, 3000)
    }

    return () => {
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
      }
    }
    // Omission of newSellerIds/isNewSellerTreatment is intentional — they
    // resolve asynchronously and re-firing on them would double-count impressions
    // oxlint-disable-next-line react/exhaustive-deps
  }, [isLoading, data?.listings, track])

  return useMemo(
    () => ({
      data: data,
      getActiveFilterCount: () => 0,
      isLoading,
      resetFilters: noop,
      setPage: noop,
      setPageSize: noop,
      setSavedSearch: noop,
      setSort: noop,
      setView: noop,
      setSortFrozen: noop,
      recordBidPlacedWhileFrozen: noop,
      state: {
        ...defaultState,
        page: 0,
        pageSize: 5,
      },
      updateFilters: noop,
      updateSearchData,
      error: null,
      dispatch: noop,
      pusherClient3MP,
      currentImpressionId,
      similarSearch: { enabled: false, data: undefined, isFetching: false },
      myBidsFilter: { isLoading: false, isError: false },
      personalizationEligibility: 'pending' as const,
    }),
    [data, isLoading, pusherClient3MP, updateSearchData, currentImpressionId]
  )
}
